import { useCallback, useState } from 'react';
import { uuid4 } from '@sentry/utils';
import { replaceOrSwapInArray, replaceOrSwapInObject } from 'utils';

const IGNORE_COLUMNS = ['_id'];

const checkIsNotIgnoreColumn = (column: string) =>
  !IGNORE_COLUMNS.includes(column);

type UseBulkUploadReviewStateParams<T extends Record<string, any>> = {
  priorityColumns?: string[];
  addMissingRequiredFields?: (row: T) => T;
};

const useBulkUploadReviewState = <T extends Record<string, any>>({
  priorityColumns = [],
  addMissingRequiredFields,
}: UseBulkUploadReviewStateParams<T>) => {
  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<T[]>([]);

  const updateKeys = useCallback(
    (data?: Record<string, any>) => {
      if (!data) {
        setColumns([]);
        return;
      }

      const responseKeys = Object.keys(data).filter(
        (el) => checkIsNotIgnoreColumn(el) && !priorityColumns.includes(el),
      );

      setColumns([...priorityColumns, ...responseKeys]);
    },
    [priorityColumns],
  );

  const updateRow = useCallback(
    ({ _id, ...rest }: Partial<T>) =>
      setRows((old) =>
        old.map((el) => (el._id === _id ? { ...el, ...rest } : el)),
      ),
    [],
  );

  const editRow = useCallback(
    (row: T) => {
      updateRow(row);
      updateKeys(row);
    },
    [updateKeys, updateRow],
  );

  const onUpdateColumn = useCallback((key: string, newKey: string) => {
    setColumns((old) => replaceOrSwapInArray(old, key, newKey));
    setRows((old) => old.map((row) => replaceOrSwapInObject(row, key, newKey)));
  }, []);

  const handleUploadRows = useCallback(
    (uploaded: T[]) => {
      const fullRows = uploaded.map((el) => {
        const elWithId = el._id ? el : { ...el, _id: uuid4() };

        return addMissingRequiredFields
          ? addMissingRequiredFields(elWithId)
          : elWithId;
      });

      updateKeys(fullRows[0]);
      setRows(fullRows);
    },
    [addMissingRequiredFields, updateKeys],
  );

  const clearRows = useCallback(() => {
    setColumns([]);
    setRows([]);
  }, [setColumns]);

  return {
    columns,
    rows,
    setColumns,
    setRows,
    updateKeys,
    updateRow,
    editRow,
    onUpdateColumn,
    handleUploadRows,
    clearRows,
  };
};

export default useBulkUploadReviewState;
